import { useCallback, useEffect, useRef, useState } from "react";
import type { SectionProps } from "./context";
import { Band, Head, cn, rv } from "./ui";
import { apiErrorText } from "@/lib/api";
import { useAuth } from "./auth";
import { goAnchor } from "./app-nav";
import { contactsApi, type Contact } from "./contacts-api";
import { SORT_DESC_DEFAULT, aed, companiesApi, takeCompany, type Company, type CompanySort, type DuplicateGroup, type OwnerCounts } from "./companies-api";
import { CompanyCard } from "./company-card";
import { locale, t } from "@/lib/i18n";

/* «Компании»: клиент = компания с людьми (закупщик, владелец, бухгалтерия) и её клиенты Zoho Books с выручкой и долгом.
   Компании приходят из Zoho CRM и создаются при связи с Books; можно завести вручную. Агент видит свои компании. */

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(locale) : "—");
const input =
  "h-9 w-full rounded-btn border border-border bg-background px-2.5 text-[13px] text-foreground outline-none transition focus:border-primary disabled:opacity-60";

function useToast() {
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  }, []);
  return { toast, flash };
}

function CompaniesDemo({ title, subtitle }: SectionProps) {
  const [openId, setOpenId] = useState<string | null>(() => takeCompany());
  const { toast, flash } = useToast();

  return (
    <Band id="companies" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv(1)} className="mt-6 overflow-hidden rounded-card border border-border bg-card shadow-sm">
        {openId ? <CompanyCard id={openId} onBack={() => setOpenId(null)} flash={flash} /> : <CompanyList onOpen={setOpenId} flash={flash} />}
      </div>
      {toast ? (
        <div role="status" className="fixed bottom-5 start-1/2 z-50 -translate-x-1/2 rounded-btn bg-foreground px-4 py-2.5 text-[13px] text-background shadow-lg">
          {toast}
        </div>
      ) : null}
    </Band>
  );
}

const COLUMNS: { key: CompanySort; label: string }[] = [
  { key: "name", label: t("Компания") },
  { key: "country", label: t("Страна") },
  { key: "segment", label: t("Сегмент") },
  { key: "people", label: t("Люди") },
  { key: "revenue", label: t("Выручка") },
  { key: "receivable", label: t("Долг") },
  { key: "last_invoice", label: t("Последний счёт") },
  { key: "owner", label: t("Ответственный") },
];

type OwnerFilter = "me" | "none" | "" | string;
const USER_FILTER_KEY = "hub.companies.owner";
/** «Пользователи → N компаний» кладут сюда сотрудника; читаем один раз при открытии списка */
function takeUserFilter(): { id: string; name: string } | null {
  try {
    const raw = sessionStorage.getItem(USER_FILTER_KEY);
    sessionStorage.removeItem(USER_FILTER_KEY);
    return raw ? (JSON.parse(raw) as { id: string; name: string }) : null;
  } catch {
    return null;
  }
}
const USER_FILTER_EVENT = "hub:companies-owner";

/** Переключатель «Мои / Без ответственного / Все» запоминается в браузере для каждого сотрудника: продавец, менеджер и
    администратор начинают с «Мои», владелец — с «Все»; выбор держится до следующего переключения. */
const MINE_KEY = (uid: string | undefined) => `hub.companies.mine:${uid ?? ""}`;
const initialMine = (user: { id: string; role: string } | null | undefined): OwnerFilter => {
  const saved = user ? localStorage.getItem(MINE_KEY(user.id)) : null;
  if (saved === "me" || saved === "none" || saved === "") return saved;
  return user?.role === "owner" ? "" : "me";
};
export function openCompaniesOf(u: { id: string; name: string }) {
  sessionStorage.setItem(USER_FILTER_KEY, JSON.stringify(u));
  window.dispatchEvent(new CustomEvent(USER_FILTER_EVENT, { detail: u })); // список уже открыт — переключаем фильтр на лету
}

function CompanyList({ onOpen, flash }: { onOpen: (id: string) => void; flash: (t: string) => void }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [byUser, setByUser] = useState(takeUserFilter);
  const [mine, setMine] = useState<OwnerFilter>(() => (byUser ? byUser.id : initialMine(user)));
  useEffect(() => {
    const on = (e: Event) => {
      const u = (e as CustomEvent<{ id: string; name: string }>).detail;
      sessionStorage.removeItem(USER_FILTER_KEY);
      setByUser(u);
      setMine(u.id);
    };
    window.addEventListener(USER_FILTER_EVENT, on);
    return () => window.removeEventListener(USER_FILTER_EVENT, on);
  }, []);
  const [sort, setSort] = useState<CompanySort>("revenue");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [rows, setRows] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<OwnerCounts | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [dupes, setDupes] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const canCreate = user?.role !== "viewer";

  useEffect(() => {
    const t = setTimeout(() => {
      companiesApi.list({ q, owner: mine, sort, dir, size: 100 }).then(
        (r) => {
          setRows(r.items);
          setTotal(r.total);
          setCounts(r.counts ?? null);
          setLoaded(true);
        },
        (e) => flash(apiErrorText(e)),
      );
    }, 250);
    return () => clearTimeout(t);
  }, [q, mine, sort, dir, flash, reloadKey]);

  // клик по заголовку: та же колонка — смена направления, другая — её привычное направление (суммы по убыванию, текст по возрастанию)
  const toggleSort = (key: CompanySort) => {
    if (key === sort) setDir(dir === "desc" ? "asc" : "desc");
    else {
      setSort(key);
      setDir(SORT_DESC_DEFAULT.has(key) ? "desc" : "asc");
    }
  };

  const create = async () => {
    if (!name.trim()) return;
    try {
      const c = await companiesApi.create({ name: name.trim() });
      onOpen(c.id);
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  return (
    <div className="p-3.5 md:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <i className="ri-search-line pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Название, TRN, почта или домен")} aria-label={t("Поиск компаний")} className={cn(input, "ps-8")} />
        </div>
        <div className="inline-flex overflow-hidden rounded-btn border border-border text-[12px]" role="group" aria-label={t("Чьи компании")}>
          {(
            [
              ["me", t("Мои"), counts?.me],
              ["none", t("Без ответственного"), counts?.none],
              ["", t("Все"), counts?.all],
            ] as [OwnerFilter, string, number | undefined][]
          ).map(([v, label, n]) => (
            <button
              key={v || "all"}
              type="button"
              onClick={() => {
                setByUser(null);
                setMine(v);
                if (user) localStorage.setItem(MINE_KEY(user.id), v);
              }}
              aria-pressed={mine === v}
              className={cn("inline-flex h-9 cursor-pointer items-center gap-1.5 px-3 font-semibold transition", mine === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
            >
              {label}
              {n !== undefined ? (
                <span className={cn("rounded-full px-1.5 text-[11px] tabular-nums", mine === v ? "bg-primary-foreground/20" : "bg-muted")}>{n.toLocaleString(locale)}</span>
              ) : null}
            </button>
          ))}
        </div>
        {byUser ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[12px] font-semibold text-primary">
            {t("Ответственный: {name}", { name: byUser.name })}
            <button type="button" aria-label={t("Сбросить")} onClick={() => { setByUser(null); setMine(""); }} className="cursor-pointer leading-none hover:text-foreground">
              ×
            </button>
          </span>
        ) : null}
        {canCreate ? (
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            className="inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            <i className="ri-add-line" aria-hidden />
            {t("Компания")}
          </button>
        ) : null}
        {canCreate ? (
          <button
            type="button"
            onClick={() => setDupes((v) => !v)}
            aria-pressed={dupes}
            className={cn(
              "inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border px-3 text-[12.5px] font-semibold transition",
              dupes ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
            )}
            data-testid="companies-duplicates"
          >
            <i className="ri-git-merge-line" aria-hidden />
            {t("Дубли")}
          </button>
        ) : null}
      </div>
      {dupes ? <Duplicates onOpen={onOpen} flash={flash} onMerged={() => setReloadKey((k) => k + 1)} /> : null}
      {creating ? (
        <form
          className="mt-3 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Название компании")} aria-label={t("Название новой компании")} className={cn(input, "max-w-sm")} />
          <button type="submit" className="h-9 cursor-pointer rounded-btn bg-primary px-3 text-[12.5px] font-semibold text-primary-foreground">
            {t("Создать")}
          </button>
        </form>
      ) : null}

      <p className="mt-3 text-[12px] text-muted-foreground">
        {loaded ? t("{n} компаний", { n: total.toLocaleString(locale) }) : t("Загрузка…")} ·{" "}
        {t("сортировка: {col}, {dir}", { col: COLUMNS.find((c) => c.key === sort)?.label ?? "", dir: dir === "desc" ? t("по убыванию") : t("по возрастанию") })}
      </p>
      <div className="mt-2 overflow-x-auto rounded-btn border border-border">
        <table className="w-full min-w-[720px] border-collapse text-start">
          <thead>
            <tr className="border-b border-border bg-background/60">
              {COLUMNS.map((h, i) => (
                <th
                  key={h.key}
                  scope="col"
                  aria-sort={sort === h.key ? (dir === "desc" ? "descending" : "ascending") : "none"}
                  className={cn("px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground", i >= 3 ? "text-end" : "text-start")}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(h.key)}
                    title={t("Сортировать по колонке «{col}»; повторный клик меняет направление", { col: h.label })}
                    className={cn("inline-flex cursor-pointer items-center gap-1 uppercase transition hover:text-foreground", sort === h.key && "text-foreground")}
                    data-testid={`sort-${h.key}`}
                  >
                    {h.label}
                    <i
                      className={cn("text-[12px] leading-none", sort === h.key ? (dir === "desc" ? "ri-arrow-down-s-fill" : "ri-arrow-up-s-fill") : "ri-expand-up-down-line opacity-40")}
                      aria-hidden
                    />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} onClick={() => onOpen(c.id)} className="cursor-pointer border-b border-border/60 transition last:border-0 hover:bg-muted/60">
                <td className="max-w-[18rem] truncate px-3 py-2 text-[12.5px] font-semibold">{c.name}</td>
                <td className="px-3 py-2 text-[12px] text-muted-foreground">{c.country || "—"}</td>
                <td className="max-w-[10rem] truncate px-3 py-2 text-[12px] text-muted-foreground">{c.segment || "—"}</td>
                <td className="px-3 py-2 text-end text-[12px] tabular-nums">{c.people || 0}</td>
                <td className="px-3 py-2 text-end text-[12px] tabular-nums">{c.revenue ? aed(c.revenue) : "—"}</td>
                <td className={cn("px-3 py-2 text-end text-[12px] tabular-nums", c.receivable > 0 && "font-semibold text-amber-600")}>{c.receivable ? aed(c.receivable) : "—"}</td>
                <td className="px-3 py-2 text-end text-[12px] text-muted-foreground">{day(c.lastInvoiceAt)}</td>
                <td className="max-w-[10rem] truncate px-3 py-2 text-[12px] text-muted-foreground">{c.owner ? c.owner.name : <span className="text-amber-600">{t("нет")}</span>}</td>
              </tr>
            ))}
            {loaded && rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">
                  {mine === "me" ? t("У вас пока нет своих компаний. Откройте «Без ответственного» или «Все» и нажмите в карточке «Это мой клиент».") : t("Компаний не найдено. Они появятся после синхронизации с Zoho (раздел «Zoho»).")}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Панель «Дубли»: группы компаний с одним названием (без правовой формы) или одним TRN. В группе отмечают, какая карточка
   остаётся (по умолчанию — с наибольшей выручкой), снимают галочки с тех, что не дубли, и жмут «Слить»: люди, сделки, заметки,
   задачи и клиенты Books переходят к оставшейся, остальные карточки удаляются. Подтверждение — вторым нажатием. */
function Duplicates({ onOpen, flash, onMerged }: { onOpen: (id: string) => void; flash: (t: string) => void; onMerged: () => void }) {
  const [groups, setGroups] = useState<DuplicateGroup[] | null>(null);
  const [keep, setKeep] = useState<Record<string, string>>({});
  const [skip, setSkip] = useState<Record<string, string[]>>({});
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    companiesApi.duplicates().then(
      (r) => {
        setGroups(r.items);
        setKeep(Object.fromEntries(r.items.map((g) => [g.key, g.keep])));
      },
      (e) => {
        flash(apiErrorText(e));
        setGroups([]);
      },
    );
  }, [flash]);

  const toMerge = (g: DuplicateGroup) => g.items.filter((x) => x.id !== keep[g.key] && !(skip[g.key] ?? []).includes(x.id));

  const merge = async (g: DuplicateGroup) => {
    const ids = toMerge(g).map((x) => x.id);
    const target = g.items.find((x) => x.id === keep[g.key]);
    if (!ids.length || !target) return;
    if (confirm !== g.key) {
      setConfirm(g.key);
      return;
    }
    setBusy(g.key);
    try {
      const r = await companiesApi.merge(target.id, ids);
      flash(t("Слито: {n} → «{name}»", { n: r.merged.length, name: r.name }));
      setGroups((gs) => (gs ?? []).filter((x) => x.key !== g.key));
      onMerged();
    } catch (e) {
      flash(apiErrorText(e));
    } finally {
      setBusy("");
      setConfirm("");
    }
  };

  const th = "px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground";
  const td = "px-2 py-1.5 text-[12px]";
  return (
    <div className="mt-3 rounded-btn border border-border bg-background/60 p-3" data-testid="companies-duplicates-panel">
      <p className="text-[12px] text-muted-foreground">
        {t("Вероятные дубли: компании с одним названием (без правовой формы) или одним TRN. Отметьте, какая карточка остаётся, снимите галочки с тех, что не дубли, и нажмите «Слить» — люди, сделки, заметки, задачи и клиенты Books перейдут к оставшейся, остальные карточки будут удалены. Аккаунты Zoho CRM удалённых карточек запоминаются, и синхронизация не создаст их заново.")}
      </p>
      {groups === null ? <p className="mt-2 text-[12px] text-muted-foreground">{t("Загрузка…")}</p> : null}
      {groups && groups.length === 0 ? <p className="mt-2 text-[12.5px] font-semibold">{t("Дублей не найдено")}</p> : null}
      {groups && groups.length > 0 ? <p className="mt-1 text-[12px] font-semibold">{t("{n} групп", { n: groups.length })}</p> : null}
      {(groups ?? []).map((g) => {
        const ids = toMerge(g);
        const target = g.items.find((x) => x.id === keep[g.key]);
        return (
          <div key={g.key} className="mt-3 overflow-x-auto rounded-btn border border-border" data-testid="duplicate-group">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className={cn(th, "text-start")}>{t("Оставить")}</th>
                  <th className={cn(th, "text-start")}>{t("Слить")}</th>
                  <th className={cn(th, "text-start")}>{g.why === "trn" ? t("один TRN") : t("одно название")}</th>
                  <th className={cn(th, "text-start")}>{t("Страна")}</th>
                  <th className={cn(th, "text-start")}>TRN</th>
                  <th className={cn(th, "text-start")}>{t("Ответственный")}</th>
                  <th className={cn(th, "text-end")}>{t("Люди")}</th>
                  <th className={cn(th, "text-end")}>{t("Сделки")}</th>
                  <th className={cn(th, "text-end")}>Books</th>
                  <th className={cn(th, "text-end")}>{t("Выручка")}</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((x) => {
                  const isKeep = keep[g.key] === x.id;
                  const checked = !isKeep && !(skip[g.key] ?? []).includes(x.id);
                  return (
                    <tr key={x.id} className={cn("border-b border-border/60 last:border-0", isKeep && "bg-primary/5")}>
                      <td className={td}>
                        <input type="radio" name={`keep-${g.key}`} checked={isKeep} onChange={() => setKeep({ ...keep, [g.key]: x.id })} aria-label={t("Оставить")} className="cursor-pointer" />
                      </td>
                      <td className={td}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isKeep}
                          onChange={(e) => setSkip({ ...skip, [g.key]: e.target.checked ? (skip[g.key] ?? []).filter((i) => i !== x.id) : [...(skip[g.key] ?? []), x.id] })}
                          aria-label={t("Слить")}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className={cn(td, "max-w-[18rem] truncate font-semibold")}>
                        <button type="button" onClick={() => onOpen(x.id)} className="cursor-pointer hover:underline">
                          {x.name}
                        </button>
                        {x.zcrmAccountId ? <span className="ms-1.5 text-[10.5px] font-normal text-muted-foreground">CRM</span> : null}
                      </td>
                      <td className={cn(td, "text-muted-foreground")}>{x.country || "—"}</td>
                      <td className={cn(td, "text-muted-foreground")}>{x.trn || "—"}</td>
                      <td className={cn(td, "max-w-[9rem] truncate text-muted-foreground")}>{x.owner || "—"}</td>
                      <td className={cn(td, "text-end tabular-nums")}>{x.people}</td>
                      <td className={cn(td, "text-end tabular-nums")}>{x.deals}</td>
                      <td className={cn(td, "text-end tabular-nums")}>{x.books}</td>
                      <td className={cn(td, "text-end tabular-nums")}>{x.revenue ? aed(x.revenue) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex flex-wrap items-center gap-2 border-t border-border px-2 py-2">
              <button
                type="button"
                onClick={() => void merge(g)}
                disabled={!ids.length || !target || busy === g.key}
                className={cn(
                  "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-btn px-3 text-[12px] font-semibold transition disabled:cursor-default disabled:opacity-50",
                  confirm === g.key ? "bg-amber-500 text-white hover:brightness-110" : "bg-primary text-primary-foreground hover:brightness-110",
                )}
                data-testid="duplicate-merge"
              >
                <i className="ri-git-merge-line" aria-hidden />
                {confirm === g.key ? t("Подтвердить: удалить {n} и оставить «{name}»", { n: ids.length, name: target?.name ?? "" }) : t("Слить {n} в «{name}»", { n: ids.length, name: target?.name ?? "" })}
              </button>
              {confirm === g.key ? (
                <button type="button" onClick={() => setConfirm("")} className="h-8 cursor-pointer rounded-btn border border-border px-3 text-[12px] font-semibold text-muted-foreground hover:text-foreground">
                  {t("Отмена")}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CompaniesDemo;
